"use client";

import { PricingTable } from "@clerk/nextjs";
import { useEffect, useRef } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MARKETING_LANDING_SECTION_IDS } from "@/lib/marketing/landing-anchors";

/** Toggle off when marketing should allow Clerk checkout / plan actions again. */
const IS_MARKETING_PRICING_TABLE_DISPLAY_ONLY = true;

export function MarketingPricingSection() {
  const pricingTableWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hasBillingJwt = currentUrl.searchParams.has("__clerk_db_jwt");
    const hasPricingHash = currentUrl.hash === `#${MARKETING_LANDING_SECTION_IDS.pricing}`;
    const shouldScrollToPricing = hasBillingJwt || hasPricingHash;

    if (!shouldScrollToPricing) {
      return;
    }

    if (hasBillingJwt && !hasPricingHash) {
      window.location.hash = MARKETING_LANDING_SECTION_IDS.pricing;
    }

    let attempts = 0;
    const maxAttempts = 8;
    const retryDelayInMs = 120;

    const scrollToPricingSection = () => {
      const pricingSection = document.getElementById(MARKETING_LANDING_SECTION_IDS.pricing);
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        return;
      }

      window.setTimeout(scrollToPricingSection, retryDelayInMs);
    };

    window.requestAnimationFrame(scrollToPricingSection);
  }, []);

  useEffect(() => {
    const el = pricingTableWrapperRef.current;
    if (!el) {
      return;
    }
    if (IS_MARKETING_PRICING_TABLE_DISPLAY_ONLY) {
      el.setAttribute("inert", "");
    } else {
      el.removeAttribute("inert");
    }
  }, []);

  return (
    <section
      aria-labelledby="pricing-heading"
      className="mt-14 scroll-mt-24"
      id={MARKETING_LANDING_SECTION_IDS.pricing}
    >
      <div className="flex flex-col gap-2">
        <h2 className="font-medium text-sm" id="pricing-heading">
          Pricing that scales with you
        </h2>
        <p className="max-w-2xl text-muted-foreground text-sm">
          Clerk-managed plans are shown below for reference. Self-serve checkout is not available
          yet—contact us for Enterprise, SLA-backed support, or custom terms.
        </p>
      </div>

      <div className="relative mt-6 min-h-[209px]">
        <div
          className={IS_MARKETING_PRICING_TABLE_DISPLAY_ONLY ? "select-none" : undefined}
          ref={pricingTableWrapperRef}
        >
          <PricingTable collapseFeatures {...{ for: "organization" }} />
        </div>
        {IS_MARKETING_PRICING_TABLE_DISPLAY_ONLY ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Work in progress. Checkout is not available yet; pricing is shown for reference only."
                  className="absolute inset-0 z-1 flex cursor-not-allowed flex-col items-center justify-start rounded-lg bg-muted/50 pt-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  type="button"
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center rounded-full border border-border bg-background/95 px-3 py-1 font-medium text-foreground text-xs shadow-sm"
                  >
                    Work in progress
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-center" side="bottom" sideOffset={8}>
                Checkout is not available yet. Plans are shown for reference only.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
    </section>
  );
}
